import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  alpha
} from '@mui/material';
import { Close, Menu as MenuIcon, NorthEast } from '@mui/icons-material';
import { useScroll } from 'framer-motion';
import { COMPANY, NAV_ITEMS } from '../../data/siteContent';
import { HEADER_HEIGHT, scrollToSection } from '../../utils/scroll';
import { MotionBox, MotionButton, useReducedMotion } from '../motion';

const Navbar = ({ activeSection }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const reducedMotion = useReducedMotion();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (v) => setProgress(Math.min(100, Math.max(0, v * 100))));
    return () => unsubscribe();
  }, [scrollYProgress]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => {
    setMobileMenuOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <LinearProgress
        variant="determinate"
        value={progress}
        aria-hidden
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          // Sits just above the AppBar, below the Drawer (1200) and Modal (1300).
          zIndex: theme.zIndex.appBar + 1,
          height: 3,
          backgroundColor: 'transparent',
          '& .MuiLinearProgress-bar': {
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            transition: 'transform 0.1s linear'
          }
        }}
      />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          height: HEADER_HEIGHT,
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: alpha(theme.palette.background.paper, scrolled ? 0.85 : 0.6),
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, scrolled ? 0.12 : 0)}`,
          boxShadow: scrolled ? `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}` : 'none',
          transition: 'background-color 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease'
          // z-index intentionally left at the theme default (appBar: 1100) so the
          // Drawer (1200) and Modal (1300) layer above it rather than under it.
        }}
      >
        <Toolbar sx={{ minHeight: { xs: HEADER_HEIGHT.xs, md: HEADER_HEIGHT.md } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
            <MotionBox
              whileHover={reducedMotion ? undefined : { scale: 1.03 }}
              whileTap={reducedMotion ? undefined : { scale: 0.97 }}
              component="button"
              onClick={() => go('home')}
              // No aria-label: WCAG 2.5.3 wants the accessible name to contain the
              // visible text, so the wordmark itself names the button.
              title={`${COMPANY.shortName}, back to top`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: { md: 4 },
                cursor: 'pointer',
                border: 0,
                background: 'none',
                p: 0
              }}
            >
              <Box
                component="img"
                src="/favicon.png"
                alt=""
                width={40}
                height={40}
                sx={{
                  width: 40,
                  height: 40,
                  mr: 1.5,
                  borderRadius: 2,
                  objectFit: 'contain',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`
                }}
              />
              <Box sx={{ textAlign: 'left' }}>
                <Typography
                  component="span"
                  sx={{ display: 'block', fontWeight: 900, color: 'primary.main', letterSpacing: 1, fontSize: '1.25rem', lineHeight: 1 }}
                >
                  {COMPANY.shortName}
                </Typography>
                <Typography
                  component="span"
                  sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', fontWeight: 500, letterSpacing: 0.5 }}
                >
                  {COMPANY.tagline}
                </Typography>
              </Box>
            </MotionBox>

            {!isMobile && (
              <Box component="nav" aria-label="Primary" sx={{ display: 'flex', gap: 0.5 }}>
                {NAV_ITEMS.map((item) => {
                  const active = activeSection === item.id;
                  return (
                    <Button
                      key={item.id}
                      onClick={() => go(item.id)}
                      aria-current={active ? 'true' : undefined}
                      disableRipple
                      sx={{
                        px: 1.75,
                        py: 1,
                        minHeight: 40,
                        color: active ? 'primary.main' : 'text.primary',
                        fontWeight: active ? 700 : 600,
                        borderRadius: 2,
                        fontSize: '0.95rem',
                        position: 'relative',
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.06), color: 'primary.main' },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: 14,
                          right: 14,
                          bottom: 6,
                          height: 2,
                          borderRadius: 2,
                          transform: active ? 'scaleX(1)' : 'scaleX(0)',
                          transformOrigin: 'center',
                          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                        }
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            )}
          </Box>

          {!isMobile && (
            <MotionButton
              whileHover={reducedMotion ? undefined : { y: -2 }}
              whileTap={reducedMotion ? undefined : { scale: 0.97 }}
              variant="contained"
              component="a"
              href={COMPANY.appUrl}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<NorthEast sx={{ fontSize: 16 }} />}
              sx={{ ml: 2, px: 2.5, py: 1, minHeight: 44, fontWeight: 700, borderRadius: 2.5 }}
            >
              Get Started
            </MotionButton>
          )}

          {isMobile && (
            <IconButton
              aria-label="Open navigation menu"
              aria-expanded={mobileMenuOpen}
              color="primary"
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                width: 44,
                height: 44,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.18) }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        slotProps={{ paper: { sx: { width: { xs: '82vw', sm: 320 }, maxWidth: 320, backgroundColor: 'background.paper' } } }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
              Navigation
            </Typography>
            <IconButton aria-label="Close navigation menu" onClick={() => setMobileMenuOpen(false)} sx={{ width: 44, height: 44 }}>
              <Close />
            </IconButton>
          </Box>

          <List component="nav" aria-label="Mobile">
            {NAV_ITEMS.map((item) => {
              const active = activeSection === item.id;
              return (
                <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => go(item.id)}
                    selected={active}
                    aria-current={active ? 'true' : undefined}
                    sx={{
                      borderRadius: 2,
                      minHeight: 48,
                      '&.Mui-selected': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          fontWeight: active ? 700 : 500,
                          color: active ? 'primary.main' : 'text.primary'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ my: 2 }} />

          <Button
            variant="contained"
            component="a"
            href={COMPANY.appUrl}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<NorthEast sx={{ fontSize: 16 }} />}
            sx={{ fontWeight: 700 }}
          >
            Get Started
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
